<template>
  <div class="new-project-card h-full shadow-sm group">
    <div
      class="preview-project-image"
      :style="{backgroundImage: `url(${project.screenshot ? project.screenshot.replace('/upload', '/upload/q_10') : ''})`}"
    >
      <div
        v-if="permissions.includes('pages')"
        class="overlay"
      >
        <b-btn
          variant="outline-light"
          @click="loadProject"
        >
          Edit Site
        </b-btn>
      </div>
    </div>

    <div class="relative items-start footer min-w-[257px]">
      <div class="mr-auto">
        <h5
          class="new-project-card-name !text-[18px] text-truncate font-[ProximaNova] !mb-[14px]"
          v-text="project.name"
        />

        <div class="flex items-center text-[#9FA9BA] text-[13px] gap-2">
          <span
            v-if="permissions.includes('pages')"
            v-b-tooltip.hover
            title="Preview Pages"
            class="text-primary hover:opacity-60 transition cursor-pointer"
            @mouseover="showPreview = true"
            @mouseleave="showPreview = false"
            @click="pagePreviewModal = true"
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
            >
              <path
                d="M1.71425 9.00519C1.61779 8.85244 1.56955 8.77607 1.54255 8.65827C1.52227 8.56979 1.52227 8.43025 1.54255 8.34177C1.56955 8.22397 1.61779 8.1476 1.71425 7.99485C2.51142 6.73262 4.88424 3.54169 8.50029 3.54169C12.1163 3.54169 14.4892 6.73262 15.2863 7.99485C15.3828 8.1476 15.431 8.22397 15.458 8.34177C15.4783 8.43025 15.4783 8.56979 15.458 8.65827C15.431 8.77607 15.3828 8.85244 15.2863 9.00519C14.4892 10.2674 12.1163 13.4584 8.50029 13.4584C4.88424 13.4584 2.51142 10.2674 1.71425 9.00519Z"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8.50029 10.625C9.67389 10.625 10.6253 9.67363 10.6253 8.50002C10.6253 7.32642 9.67389 6.37502 8.50029 6.37502C7.32668 6.37502 6.37529 7.32642 6.37529 8.50002C6.37529 9.67363 7.32668 10.625 8.50029 10.625Z"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>

          <div class="font-[ProximaNova] leading-normal mt-[2px]">
            <strong>{{ project.pages_count }}</strong> Page{{ project.pages_count > 1 ? 's' : '' }}
          </div>
        </div>

        <div class="flex items-center border-y border-[#EEF0F3] mx-[-25px] my-3">
          <button
            v-for="(link, index) in projectLinks"
            v-if="link.show"
            :key="index"
            v-b-tooltip.hover
            :title="link.name"
            class="w-full h-[40px] flex items-center justify-center p-0 color-[#44474A] hover:!bg-[#EEF0F3] transition-colors"
            @click="openLink(link)"
            v-html="link.icon"
          />

          <div class="relative on-hover select-none w-full h-[40px]">
            <div class="w-full h-[40px] flex items-center justify-center p-0 color-[#44474A] hover:!bg-[#EEF0F3] transition-colors">
              <svg
                width="18"
                height="17"
                viewBox="0 0 18 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5"
              >
                <path
                  d="M9.00065 4.24998C9.39185 4.24998 9.70898 3.93285 9.70898 3.54165C9.70898 3.15044 9.39185 2.83331 9.00065 2.83331C8.60945 2.83331 8.29232 3.15044 8.29232 3.54165C8.29232 3.93285 8.60945 4.24998 9.00065 4.24998Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M9.00065 9.20831C9.39185 9.20831 9.70898 8.89118 9.70898 8.49998C9.70898 8.10878 9.39185 7.79165 9.00065 7.79165C8.60945 7.79165 8.29232 8.10878 8.29232 8.49998C8.29232 8.89118 8.60945 9.20831 9.00065 9.20831Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M9.00065 14.1666C9.39185 14.1666 9.70898 13.8495 9.70898 13.4583C9.70898 13.0671 9.39185 12.75 9.00065 12.75C8.60945 12.75 8.29232 13.0671 8.29232 13.4583C8.29232 13.8495 8.60945 14.1666 9.00065 14.1666Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M13.959 4.24998C14.3502 4.24998 14.6673 3.93285 14.6673 3.54165C14.6673 3.15044 14.3502 2.83331 13.959 2.83331C13.5678 2.83331 13.2507 3.15044 13.2507 3.54165C13.2507 3.93285 13.5678 4.24998 13.959 4.24998Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M13.959 9.20831C14.3502 9.20831 14.6673 8.89118 14.6673 8.49998C14.6673 8.10878 14.3502 7.79165 13.959 7.79165C13.5678 7.79165 13.2507 8.10878 13.2507 8.49998C13.2507 8.89118 13.5678 9.20831 13.959 9.20831Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M13.959 14.1666C14.3502 14.1666 14.6673 13.8495 14.6673 13.4583C14.6673 13.0671 14.3502 12.75 13.959 12.75C13.5678 12.75 13.2507 13.0671 13.2507 13.4583C13.2507 13.8495 13.5678 14.1666 13.959 14.1666Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M4.04232 4.24998C4.43352 4.24998 4.75065 3.93285 4.75065 3.54165C4.75065 3.15044 4.43352 2.83331 4.04232 2.83331C3.65112 2.83331 3.33398 3.15044 3.33398 3.54165C3.33398 3.93285 3.65112 4.24998 4.04232 4.24998Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M4.04232 9.20831C4.43352 9.20831 4.75065 8.89118 4.75065 8.49998C4.75065 8.10878 4.43352 7.79165 4.04232 7.79165C3.65112 7.79165 3.33398 8.10878 3.33398 8.49998C3.33398 8.89118 3.65112 9.20831 4.04232 9.20831Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
                <path
                  d="M4.04232 14.1666C4.43352 14.1666 4.75065 13.8495 4.75065 13.4583C4.75065 13.0671 4.43352 12.75 4.04232 12.75C3.65112 12.75 3.33398 13.0671 3.33398 13.4583C3.33398 13.8495 3.65112 14.1666 4.04232 14.1666Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="currentColor"
                />
              </svg>
            </div>

            <div class="overlay absolute top-0 right-0 translate-y-[40px] z-[100] !w-[56px] bg-white rounded border border-[#E2E5EC] shadow-[0_1px_12px_rgba(0,0,0,0.03)]">
              <button
                v-for="(link, index) in settingsLinks"
                v-if="link.show"
                :key="index"
                v-b-tooltip.right.hover
                :title="link.name"
                class="!w-[56px] !h-[40px] flex items-center justify-center p-0 color-[#44474A] hover:!bg-[#EEF0F3] transition-colors cursor-pointer"
                @click="openLink(link)"
                v-html="link.icon"
              />
            </div>
          </div>
        </div>

        <div class="flex font-[ProximaNova] mb-2 overflow-hidden">
          <div class="xl:mr-5 lg:mr-2">
            <div class="text-[15px] mb-[10px]">
              Status:
            </div>
            <div class="text-[15px] mb-[10px]">
              Domain:
            </div>
            <div
              class="text-[15px] mb-[10px]"
            >
              Description:
            </div>
          </div>

          <div>
            <div class="new-project-card-status mb-[10px]">
              <div
                v-if="project.status"
                class="d-flex align-items-center"
              >
                <span class="published-indicator mr-2" /> Published
              </div>
              <div v-else>
                Not Published
              </div>
            </div>
            <div class="new-project-card-status mb-[10px]">
              <a
                :href="'https://' + (project.domain || project.subdomain +'.estage.site')"
                target="_blank"
                class="text-[#4F83E3] font-[600] text-truncate block xl:max-w-[220px] md:max-w-[120px]"
              >{{ project.domain || `${project.subdomain}.estage.site` }}</a>
            </div>
            <div
              class="new-project-card-status text-truncate xl:max-w-[220px] md:max-w-[120px] mb-[10px]"
            >
              {{ project.description || 'No Description' }}
            </div>
          </div>
        </div>

        <div class="text-[#9FA9BA] text-[13px] font-[ProximaNova]">
          Last modified: {{ project.updated_at | moment("MMM DD YYYY - h.MMA") }}
        </div>
      </div>
    </div>

    <PageLinksPreview
      v-model="pagePreviewModal"
      :project="project"
    />
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {addhttp} from '@/utils/urlHelper'
import PageLinksPreview from '@/components/editor/modals/PageLinksPreview.vue'

export default {
  name: 'ArchivedProjects',
  components: {
    PageLinksPreview
  },
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      showPreview: false,
      pagePreviewModal: false,
      permissions: []
    }
  },
  computed: {
    ...mapGetters('auth', {
      userRole: 'userRole'
    }),
    projectLinks () {
      return [
        {
          show: true,
          name: 'Admin & Modules',
          route: 'admin-modules',
          icon: `
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[18px] h-[18px]">
            <g clip-path="url(#clip0)">
            <path d="M20.237 0.462758C20.3585 0.620008 20.541 0.702641 20.7254 0.702641C20.8574 0.702641 20.99 0.660707 21.1022 0.573757C21.3717 0.36594 21.4216 -0.0213263 21.2132 -0.29081C20.3733 -1.37861 19.3947 -2.35664 18.3056 -3.19778C18.0361 -3.4056 17.6495 -3.35626 17.4404 -3.08678C17.2326 -2.81668 17.282 -2.43003 17.5514 -2.22221C18.5578 -1.44521 19.4613 -0.541793 20.237 0.462758Z" fill="#7E7E7E"/>
            <path d="M7.13964 3.94996L3.58517 2.17273L0.0307007 3.94996L3.58517 5.72719L7.13964 3.94996Z" fill="currentColor"/>
            <path d="M12.8352 12.3478L9.13516 14.1978V17.7522L12.8352 15.9022V12.3478Z" fill="currentColor"/>
            <path d="M9.89737 3.94996L13.4518 5.72719L17.0063 3.94996L13.4518 2.17273L9.89737 3.94996Z" fill="currentColor"/>
            <path d="M14.0685 10.3522L17.7685 8.50222V4.94775L14.0685 6.79776V10.3522Z" fill="currentColor"/>
            <path d="M7.90184 4.94775L4.20184 6.79776V10.3522L7.90184 8.50222V4.94775Z" fill="currentColor"/>
            <path d="M12.8352 6.79776L9.13516 4.94775V8.50222L12.8352 10.3522V6.79776Z" fill="currentColor"/>
            <path d="M12.0724 11.35L8.5185 9.57275L4.96404 11.35L8.5185 13.1272L12.0724 11.35Z" fill="currentColor"/>
            <path d="M4.20184 15.9022L7.90184 17.7522V14.1978L4.20184 12.3478V15.9022Z" fill="currentColor"/>
            <path d="M-0.731506 8.50222L2.96849 10.3522V6.79776L-0.731506 4.94775V8.50222Z" fill="currentColor"/>
            </g>
            <defs>
            <clipPath id="clip0">
            <rect width="37" height="37" fill="white" transform="translate(-10 -9)"/>
            </clipPath>
            </defs>
          </svg>
          `
        },
        {
          show: this.permissions.includes('crm'),
          name: 'CRM',
          href: `https://crm.estage.com/${this.project.id}`,
          icon: `
          <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[18px] h-[18px]">
            <path d="M3.00002 16.8631C3.45195 17 4.06237 17 5.1 17H12.9C13.9376 17 14.548 17 15 16.8631M3.00002 16.8631C2.90312 16.8337 2.8135 16.7981 2.72852 16.7548C2.30516 16.539 1.96095 16.1948 1.74524 15.7715C1.5 15.2902 1.5 14.6601 1.5 13.4V5.6C1.5 4.33988 1.5 3.70982 1.74524 3.22852C1.96095 2.80516 2.30516 2.46095 2.72852 2.24524C3.20982 2 3.83988 2 5.1 2H12.9C14.1601 2 14.7902 2 15.2715 2.24524C15.6948 2.46095 16.039 2.80516 16.2548 3.22852C16.5 3.70982 16.5 4.33988 16.5 5.6V13.4C16.5 14.6601 16.5 15.2902 16.2548 15.7715C16.039 16.1948 15.6948 16.539 15.2715 16.7548C15.1865 16.7981 15.0969 16.8337 15 16.8631M3.00002 16.8631C3.00026 16.2561 3.0039 15.9349 3.05764 15.6647C3.29436 14.4747 4.22466 13.5444 5.41473 13.3076C5.70453 13.25 6.05302 13.25 6.75 13.25H11.25C11.947 13.25 12.2955 13.25 12.5853 13.3076C13.7753 13.5444 14.7056 14.4747 14.9424 15.6647C14.9961 15.9349 14.9997 16.2561 15 16.8631M12 7.625C12 9.28185 10.6569 10.625 9 10.625C7.34315 10.625 6 9.28185 6 7.625C6 5.96815 7.34315 4.625 9 4.625C10.6569 4.625 12 5.96815 12 7.625Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('blog'),
          name: 'Blog',
          route: 'settings.blog',
          icon: `
          <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[18px] h-[18px]">
            <path d="M1.875 16.625L6.03695 15.0242C6.30316 14.9218 6.43626 14.8707 6.56079 14.8038C6.6714 14.7444 6.77685 14.6759 6.87603 14.5989C6.98769 14.5123 7.08853 14.4115 7.29021 14.2098L15.75 5.74998C16.5784 4.92156 16.5784 3.57841 15.75 2.74998C14.9216 1.92156 13.5784 1.92156 12.75 2.74998L4.29021 11.2098C4.08853 11.4115 3.98769 11.5123 3.90104 11.624C3.82408 11.7231 3.75555 11.8286 3.69618 11.9392C3.62933 12.0637 3.57814 12.1968 3.47575 12.463L1.875 16.625ZM1.875 16.625L3.41859 12.6117C3.52905 12.3245 3.58428 12.1809 3.67901 12.1151C3.76179 12.0577 3.86423 12.0359 3.96322 12.0548C4.0765 12.0765 4.18529 12.1853 4.40286 12.4028L6.09718 14.0971C6.31475 14.3147 6.42354 14.4235 6.44517 14.5368C6.46408 14.6358 6.44234 14.7382 6.38486 14.821C6.31908 14.9157 6.17549 14.971 5.8883 15.0814L1.875 16.625Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('community'),
          name: 'Community',
          route: 'project.community',
          icon: `
          <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[18px] h-[18px]">
            <path d="M16.5 16.25V14.75C16.5 13.3521 15.5439 12.1775 14.25 11.8445M11.625 2.96807C12.7244 3.41311 13.5 4.49098 13.5 5.75C13.5 7.00902 12.7244 8.08689 11.625 8.53193M12.75 16.25C12.75 14.8522 12.75 14.1533 12.5216 13.6019C12.2172 12.8669 11.6331 12.2828 10.8981 11.9784C10.3467 11.75 9.64783 11.75 8.25 11.75H6C4.60218 11.75 3.90326 11.75 3.35195 11.9784C2.61687 12.2828 2.03284 12.8669 1.72836 13.6019C1.5 14.1533 1.5 14.8522 1.5 16.25M10.125 5.75C10.125 7.40685 8.78185 8.75 7.125 8.75C5.46815 8.75 4.125 7.40685 4.125 5.75C4.125 4.09315 5.46815 2.75 7.125 2.75C8.78185 2.75 10.125 4.09315 10.125 5.75Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('marketing_funnels'),
          name: 'Funnels',
          href: `https://marketing-funnel.estage.com?project=${this.project.id}`,
          icon: `
          <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[18px] h-[18px]">
            <path d="M1.5 3.95C1.5 3.52996 1.5 3.31994 1.58175 3.15951C1.65365 3.01839 1.76839 2.90365 1.90951 2.83175C2.06994 2.75 2.27996 2.75 2.7 2.75H15.3C15.72 2.75 15.9301 2.75 16.0905 2.83175C16.2316 2.90365 16.3463 3.01839 16.4183 3.15951C16.5 3.31994 16.5 3.52996 16.5 3.95V4.45205C16.5 4.65364 16.5 4.75444 16.4754 4.84817C16.4535 4.93123 16.4176 5.00993 16.3691 5.08082C16.3144 5.16082 16.2383 5.22684 16.0859 5.35887L11.2891 9.51613C11.1367 9.64816 11.0606 9.71418 11.0059 9.79418C10.9574 9.86507 10.9215 9.94377 10.8996 10.0268C10.875 10.1206 10.875 10.2214 10.875 10.423V14.3438C10.875 14.4905 10.875 14.5638 10.8513 14.6272C10.8304 14.6832 10.7964 14.7334 10.7522 14.7736C10.7021 14.8192 10.634 14.8464 10.4978 14.9009L7.94783 15.9209C7.67218 16.0311 7.53435 16.0863 7.4237 16.0633C7.32695 16.0432 7.24204 15.9857 7.18744 15.9033C7.125 15.8091 7.125 15.6607 7.125 15.3638V10.423C7.125 10.2214 7.125 10.1206 7.10037 10.0268C7.07854 9.94377 7.0426 9.86507 6.99413 9.79418C6.93943 9.71418 6.86326 9.64816 6.71092 9.51613L1.91408 5.35887C1.76174 5.22684 1.68557 5.16082 1.63087 5.08082C1.5824 5.00993 1.54646 4.93123 1.52463 4.84817C1.5 4.75444 1.5 4.65364 1.5 4.45205V3.95Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        }
      ]
    },
    settingsLinks () {
      return [
        {
          show: this.permissions.includes('video_hosting'),
          name: 'Video Hosting',
          href: 'https://video.estage.com',
          icon: `
          <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5">
            <path d="M9 17C13.1421 17 16.5 13.6421 16.5 9.5C16.5 5.35786 13.1421 2 9 2C4.85786 2 1.5 5.35786 1.5 9.5C1.5 13.6421 4.85786 17 9 17Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7.125 7.224C7.125 6.86604 7.125 6.68705 7.19981 6.58713C7.265 6.50006 7.36478 6.44558 7.47328 6.43783C7.59779 6.42893 7.74834 6.52572 8.04945 6.71929L11.5899 8.99529C11.8512 9.16326 11.9818 9.24725 12.027 9.35404C12.0664 9.44735 12.0664 9.55265 12.027 9.64596C11.9818 9.75275 11.8512 9.83674 11.5899 10.0047L8.04946 12.2807C7.74834 12.4743 7.59779 12.5711 7.47328 12.5622C7.36478 12.5544 7.265 12.4999 7.19981 12.4129C7.125 12.3129 7.125 12.134 7.125 11.776V7.224Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('domain_management'),
          name: 'Domain Management',
          route: 'domain.management',
          icon: `
          <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5">
            <path d="M16.5826 2.72685C14.8242 0.968422 12.4862 0 9.99945 0C7.51267 0 5.17473 0.968422 3.4163 2.72685C1.65788 4.48527 0.689453 6.82321 0.689453 9.31C0.689453 11.7968 1.65784 14.1347 3.41627 15.8932C5.17473 17.6516 7.51267 18.62 9.99945 18.62C12.4862 18.62 14.8242 17.6516 16.5826 15.8932C18.341 14.1347 19.3095 11.7968 19.3095 9.31C19.3095 6.82321 18.341 4.48527 16.5826 2.72685ZM17.1558 13.1993H13.9205C14.131 12.1688 14.2561 11.0503 14.2876 9.89188H18.1247C18.0402 11.0826 17.699 12.2036 17.1558 13.1993ZM9.99945 17.4563C9.12719 17.4563 8.1964 16.2964 7.5782 14.363H12.4207C11.8025 16.2964 10.8717 17.4563 9.99945 17.4563ZM7.26653 13.1993C7.05077 12.2228 6.90977 11.1092 6.8754 9.89188H13.1235C13.0892 11.1092 12.9482 12.2228 12.7324 13.1993H7.26653ZM1.87382 9.89188H5.71136C5.74282 11.0503 5.86792 12.1688 6.07842 13.1993H2.83784C2.28583 12.1861 1.95594 11.0634 1.87382 9.89188ZM2.84315 5.42075H6.07838C5.86792 6.45121 5.74282 7.56968 5.71133 8.72813H1.87422C1.95867 7.53735 2.29987 6.41641 2.84315 5.42075ZM9.99945 1.16375C10.8717 1.16375 11.8025 2.32365 12.4207 4.257H7.5782C8.1964 2.32365 9.12719 1.16375 9.99945 1.16375ZM12.7324 5.42075C12.9481 6.39724 13.0891 7.51081 13.1235 8.72813H6.8754C6.90977 7.51081 7.05077 6.39724 7.26653 5.42075H12.7324ZM14.2875 8.72813C14.2561 7.56968 14.131 6.45121 13.9205 5.42075H17.1557C17.699 6.41641 18.0402 7.53735 18.1246 8.72813H14.2875ZM16.3845 4.257H13.6355C13.4975 3.7795 13.3392 3.32847 13.1613 2.91043C12.9362 2.38151 12.6854 1.91961 12.4134 1.52888C13.998 2.02147 15.3765 2.98596 16.3845 4.257ZM7.58547 1.52888C7.31352 1.91961 7.06266 2.38151 6.83758 2.91043C6.65967 3.32847 6.5014 3.77946 6.36343 4.257H3.61439C4.62238 2.98596 6.00092 2.02151 7.58547 1.52888ZM3.60897 14.363H6.36343C6.50137 14.8405 6.65967 15.2915 6.83758 15.7096C7.06342 16.2402 7.31523 16.7034 7.58824 17.0949C6.33735 16.7092 5.19117 16.0222 4.23922 15.0703C4.014 14.8451 3.80405 14.6087 3.60897 14.363ZM12.4134 17.0911C12.6854 16.7004 12.9362 16.2385 13.1613 15.7096C13.3392 15.2915 13.4975 14.8405 13.6355 14.363H16.3845C15.3765 15.634 13.998 16.5985 12.4134 17.0911Z" fill="currentColor"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('comments'),
          name: 'Comments',
          route: 'project.comments',
          icon: `
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5">
            <path d="M7.0625 9.8075H7.07125M11 9.8075H11.0088M14.9375 9.8075H14.9463M6.625 16.37V18.4136C6.625 18.8798 6.625 19.1129 6.72058 19.2327C6.80369 19.3368 6.92974 19.3974 7.06297 19.3972C7.21617 19.3971 7.39821 19.2514 7.76229 18.9602L9.84956 17.2903C10.276 16.9492 10.4891 16.7787 10.7266 16.6574C10.9372 16.5498 11.1614 16.4711 11.3931 16.4236C11.6542 16.37 11.9272 16.37 12.4733 16.37H14.675C16.1451 16.37 16.8802 16.37 17.4417 16.0839C17.9356 15.8322 18.3372 15.4306 18.5889 14.9367C18.875 14.3752 18.875 13.6401 18.875 12.17V7.445C18.875 5.97486 18.875 5.23979 18.5889 4.67827C18.3372 4.18434 17.9356 3.78277 17.4417 3.5311C16.8802 3.245 16.1451 3.245 14.675 3.245H7.325C5.85486 3.245 5.11979 3.245 4.55827 3.5311C4.06435 3.78277 3.66278 4.18434 3.41111 4.67827C3.125 5.23979 3.125 5.97486 3.125 7.44499V12.87C3.125 13.6837 3.125 14.0906 3.21444 14.4244C3.45717 15.3303 4.16473 16.0378 5.0706 16.2806C5.40441 16.37 5.81127 16.37 6.625 16.37ZM7.5 9.8075C7.5 10.0491 7.30412 10.245 7.0625 10.245C6.82088 10.245 6.625 10.0491 6.625 9.8075C6.625 9.56587 6.82088 9.37 7.0625 9.37C7.30412 9.37 7.5 9.56587 7.5 9.8075ZM11.4375 9.8075C11.4375 10.0491 11.2416 10.245 11 10.245C10.7584 10.245 10.5625 10.0491 10.5625 9.8075C10.5625 9.56587 10.7584 9.37 11 9.37C11.2416 9.37 11.4375 9.56587 11.4375 9.8075ZM15.375 9.8075C15.375 10.0491 15.1791 10.245 14.9375 10.245C14.6959 10.245 14.5 10.0491 14.5 9.8075C14.5 9.56587 14.6959 9.37 14.9375 9.37C15.1791 9.37 15.375 9.56587 15.375 9.8075Z" stroke="#44474A" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('modal'),
          name: 'Modal Builder',
          route: 'module.modal',
          icon: `
          <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5">
            <rect x="0.694922" y="1.27" width="11.6091" height="10.7" rx="1.35" fill="#EEF0F3" stroke="#44474A" stroke-width="1.3"/>
            <rect x="5.69492" y="5.27" width="11.6091" height="10.7" rx="1.35" fill="#EEF0F3" stroke="#44474A" stroke-width="1.3"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('mega_menu'),
          name: 'Mega Menu Builder',
          route: 'module.mega-menu',
          icon: `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5">
            <path d="M2.875 10.12H17.125M2.875 5.37H17.125M2.875 14.87H17.125" stroke="#44474A" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `
        },
        {
          show: this.permissions.includes('members'),
          name: 'Users Management',
          route: 'settings.permissions.users',
          icon: `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
            <path d="M10 15H6a4 4 0 0 0-4 4v2"/>
            <path d="m14.305 16.53.923-.382"/>
            <path d="m15.228 13.852-.923-.383"/>
            <path d="m16.852 12.228-.383-.923"/>
            <path d="m16.852 17.772-.383.924"/>
            <path d="m19.148 12.228.383-.923"/>
            <path d="m19.53 18.696-.382-.924"/>
            <path d="m20.772 13.852.924-.383"/>
            <path d="m20.772 16.148.924.383"/>
            <circle cx="18" cy="15" r="3"/>
            <circle cx="9" cy="7" r="4"/>
          </svg>
          `
        }
      ]
    }
  },
  methods: {
    getSiteUrl () {
      return this.project.domain ? addhttp(this.project.domain) : `https://${this.project.subdomain}.estage.site`
    },
    async loadProject () {
      this.$store.commit('projects/RESET')
      this.$store.state.pages.page = {}
      this.$store.commit('filter/SET_BY_NAME', '')
      await this.$router.push(`/builder/${this.project.id}`)
    },
    async openLink (link) {
      if (link.route) {
        await this.$router.push({name: link.route, params: {id: this.project.id}})
      } else if (link.href) {
        window.open(link.href, '_blank')
      } else if (link.action) {
        await this[link.action]()
      }
    }
  },
  async mounted () {
    const {role} = await this.$store.dispatch('roles/getProjectPermissions', this.project.id)
    if (role.permissions.length > 0) {
      this.permissions = role.permissions.map(item => item.permissions_key)
    }
  }
}
</script>